/**
 * Componente que exibe status da API (chama GET /api/health).
 * Usa TanStack Query (M0.E4.S1.5).
 * Atende M0.E4.S2.2 (componente <HealthStatus /> com refetch a cada 30s).
 */

import { useQuery } from "@tanstack/react-query";

import { cn } from "@/lib/utils";

interface HealthResponse {
  status: "ok" | "degraded";
  version: string;
  environment: string;
  db?: "ok" | "fail";
  redis?: "ok" | "fail";
  storage?: "ok" | "fail";
}

async function fetchHealth(): Promise<HealthResponse> {
  const res = await fetch("/api/health");
  if (!res.ok && res.status !== 503) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as HealthResponse;
}

export function HealthStatus() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["health"],
    queryFn: fetchHealth,
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return (
      <span className="text-xs text-[var(--color-muted-foreground)]">
        verificando…
      </span>
    );
  }

  if (error || !data) {
    return (
      <span className="inline-flex items-center gap-2 rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-1 text-xs text-[var(--color-destructive)]">
        <span className="h-2 w-2 rounded-full bg-[var(--color-destructive)]" />
        API inacessível
      </span>
    );
  }

  const ok = data.status === "ok";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 rounded-md border px-3 py-1 font-mono text-xs",
        ok
          ? "border-[var(--color-success)] bg-[var(--color-success)]/10 text-[var(--color-success)]"
          : "border-[var(--color-warning)] bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
      )}
    >
      <span
        className={cn(
          "h-2 w-2 rounded-full",
          ok ? "bg-[var(--color-success)]" : "bg-[var(--color-warning)]",
        )}
      />
      API {data.status} · v{data.version}
    </span>
  );
}
